import chunk from 'lodash/chunk';
import memoize from 'lodash/memoize';
import qs from 'qs';

import { definitions, operations, paths } from '@luxuryescapes/contract-search';

import { RestoreType } from '~/components/Content/RestoreSearchDashboard/RestoreSearchComponents/types';

import { getCurrencyCodeByRegionCode } from '~/utils/getCurrencyCodeByRegionCode';
import { isLePlaceId } from '~/utils/lePlaceId';

import PublicOfferService, { readPublicOfferFields } from './PublicOfferService';
import { request } from './common';

export type GlobalRanks = Record<string, number>;

export type TypeaheadResult = {
  format: {
    mainText: string;
    secondaryText?: string;
    imageId?: string;
  };
  destinationType?: string;
  id: string;
  name: string;
  searchType: 'destination' | 'landmark' | 'property' | 'experience' | 'departurePort' | 'airport' | 'tour' | 'bounds';
  offerId?: string;
  country_code?: string | null;
};
export type TypeaheadResponse = { result: Array<TypeaheadResult> };

export type PlaceAncestorsResponse =
  paths['/api/search/place/v1/places/{id}/ancestors']['get']['responses']['200']['content']['application/json'];

export type GetPlaceResponse =
  paths['/api/search/admin/v1/place/{id}']['get']['responses']['200']['content']['application/json'];

export type GetPlaceDetailsResponse =
  operations['/api/search/place/v1/places/{id}/get']['responses']['200']['content']['application/json'];

export type GetPlacesResponse =
  operations['/api/search/place/v1/places/get']['responses']['200']['content']['application/json'];

export type GetTypeaheadDeprioritisationsResponse =
  paths['/api/search/admin/v1/typeahead/deprioritisations']['get']['responses']['200']['content']['application/json'];

export type HotelSearchList = definitions['hotelListOffersResult'];

export interface NearbyHotelOffer {
  offerId: string;
  propertyId: string;
  title: string;
  offerType: 'hotel' | 'tactical_ao_hotel' | 'bundle_and_save' | 'bedbank_hotel';
  location: {
    latitude: number;
    longitude: number;
  };
  distanceMeters: number;
}

export interface NearbyHotelOffersParams {
  latitude: number;
  longitude: number;
  radiusMeters: number;
  limit?: number;
  cursor?: string;
}

export interface NearbyHotelOffersResponse {
  status?: 200;
  result: Array<NearbyHotelOffer>;
  total: number;
  nextCursor?: string;
}

export type TourSearchList = definitions['tourListOffersResult'];

export type CruiseSearchList =
  paths['/api/search/cruise/v1/list']['post']['responses']['200']['content']['application/json']['result'];

export type UnifiedSearchList =
  paths['/api/search/unified/v1/list']['get']['responses']['200']['content']['application/json']['results'];

export type OrderOffer = UnifiedSearchList[0] & { curationType?: 'fixed' | 'dynamic'; endDate?: string | null };

export interface LuxPlusOffersBySavingsParams {
  brand?: string;
  region?: string;
  priceMin?: number;
  priceMax?: number;
  savingMin?: number;
  savingMax?: number;
  limit?: number;
  hotelTypes?: Array<LuxPlusHotelOfferType>;
  reviewStatus?: 'all' | 'flagged' | 'not_flagged';
}

export interface LuxPlusOfferBySavings {
  offerId: string;
  offerName: string;
  hotelTypeRaw: LuxPlusHotelOfferType;
  hotelType: string;
  nonMemberPrice: number;
  luxPlusPrice: number;
  saving: number;
  flaggedForReview: boolean;
  reviewSavingThreshold?: number;
  currencyCode: string;
  duration?: number;
  packageId?: string;
  roomRateId?: string;
}

export interface LuxPlusOffersBySavingsResponse {
  status: 200;
  result: {
    offers: Array<LuxPlusOfferBySavings>;
    total: number;
  };
}

type HotelSearchOffer = Extract<HotelSearchList[number], { vertical: 'hotel' }>;

type SearchLightOffer = HotelSearchOffer & {
  name?: string;
  title?: string;
};

type PublicOfferLookupRegion = Parameters<typeof PublicOfferService.getMultipleOffersById>[1];
type PublicOfferLookupBrand = Parameters<typeof PublicOfferService.getMultipleOffersById>[2];

export type LuxPlusHotelOfferType = HotelSearchOffer['type'];

export const LUX_PLUS_OFFERS_BY_SAVINGS_HOTEL_TYPES: Array<LuxPlusHotelOfferType> = ['hotel', 'tactical_ao_hotel'];

export const LUX_PLUS_OFFERS_BY_SAVINGS_HOTEL_TYPE_LABELS: Record<LuxPlusHotelOfferType, string> = {
  hotel: 'Flash',
  tactical_ao_hotel: 'LPC',
  bundle_and_save: 'Bundle & Save',
  bedbank_hotel: 'Bedbank',
  rental: 'Rental',
};

const OFFER_NAME_LOOKUP_CHUNK_SIZE = 50;
const PLACE_LOOKUP_CHUNK_SIZE = 100;
const BEDBANK_NAME_LOOKUP_CONCURRENCY = 8;

export const LUX_PLUS_REVIEW_SAVING_THRESHOLDS = [
  { priceMin: 0, priceMax: 499, savingThreshold: 15 },
  { priceMin: 500, priceMax: 1499, savingThreshold: 30 },
  { priceMin: 1500, priceMax: 2999, savingThreshold: 50 },
  { priceMin: 3000, priceMax: 9999, savingThreshold: 100 },
  { priceMin: 10000, savingThreshold: 150 },
];

function getLuxPlusReviewSavingThreshold(nonMemberPrice: number): number | undefined {
  return LUX_PLUS_REVIEW_SAVING_THRESHOLDS.find(
    (threshold) =>
      nonMemberPrice >= threshold.priceMin &&
      (threshold.priceMax === undefined || nonMemberPrice <= threshold.priceMax),
  )?.savingThreshold;
}

async function mapWithConcurrency<T, R>(
  items: Array<T>,
  concurrency: number,
  mapper: (item: T) => Promise<R>,
): Promise<Array<R>> {
  const results: Array<R> = [];

  for (let index = 0; index < items.length; index += concurrency) {
    const batch = items.slice(index, index + concurrency);
    results.push(...(await Promise.all(batch.map(mapper))));
  }

  return results;
}

async function getLuxPlusOfferNamesById({
  rows,
  region,
  brand,
  signal,
}: {
  rows: Array<LuxPlusOfferBySavings>;
  region: string;
  brand: string;
  signal?: AbortSignal;
}): Promise<Map<string, string>> {
  const namesById = new Map<string, string>();
  const rowsMissingNames = rows.filter((row) => row.offerName === row.offerId);
  const leOfferIds = rowsMissingNames.filter((row) => row.hotelTypeRaw !== 'bedbank_hotel').map((row) => row.offerId);
  const bedbankOfferIds = rowsMissingNames
    .filter((row) => row.hotelTypeRaw === 'bedbank_hotel')
    .map((row) => row.offerId);

  await Promise.all(
    chunk(leOfferIds, OFFER_NAME_LOOKUP_CHUNK_SIZE).map(async (offerIds) => {
      try {
        const offers = await PublicOfferService.getMultiplePublicOffersById(
          {
            offerIds: offerIds.join(','),
            region: region as PublicOfferLookupRegion,
            brand: brand as PublicOfferLookupBrand,
          },
          { signal },
        );

        offers.result.forEach((offer) => {
          const { ids, offerName } = readPublicOfferFields(offer);
          const id = ids[0];

          if (id && offerName) {
            namesById.set(id, offerName);
          }
        });
      } catch {
        if (signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }

        // Keep the offer ID fallback if a public-offer lookup fails for this chunk.
      }
    }),
  );

  await mapWithConcurrency(bedbankOfferIds, BEDBANK_NAME_LOOKUP_CONCURRENCY, async (offerId) => {
    try {
      const offer = await PublicOfferService.getBedbankOfferById(offerId, region, brand as App.Tenant['brand'], {
        signal,
      });
      const name = offer.result.name;

      if (name) {
        namesById.set(offerId, name);
      }
    } catch {
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      // Keep the offer ID fallback if an individual bedbank lookup fails.
    }
  });

  return namesById;
}

type UnifiedSearchOfferType = OrderOffer['offerType'];

export type HotelScoreResponse =
  paths['/api/search/admin/v1/hotel/scores/{id}']['get']['responses']['200']['content']['application/json'];

export type AdminOfferScoreResponse =
  paths['/api/search/admin/v1/{vertical}/scores/{id}/{region}/{variant}']['get']['responses']['200']['content']['application/json'];

export type AdminOfferScore = AdminOfferScoreResponse['result']['score'];

export type HotelAdminScore = Extract<AdminOfferScore, { viewsCount: number }>;

export type HotelScoreForVariantResponse = Omit<AdminOfferScoreResponse, 'result'> & {
  result: {
    score: HotelAdminScore;
  };
};

export function isHotelAdminScore(score: object): score is HotelAdminScore {
  return 'viewsCount' in score;
}

export type ScoreSettings =
  paths['/api/search/admin/v1/{vertical}/scoreSettings']['get']['responses']['200']['content']['application/json']['result'];

export const TYPEAHEAD_TYPES = [
  'airport',
  'province_state',
  'neighborhood',
  'city',
  'high_level_region',
  'country',
  'multi_city_vicinity',
  'metro_station',
  'continent',
  'train_station',
  'point_of_interest',
  'colloquial_area',
  'le_property',
  'bedbank_property',
  'le_property_unique_stays',
  'bedbank_property_unique_stays',
  'le_property_rental',
  'bedbank_property_rental',
  'le_experience',
  'channel_experience',
  'car_hire_location',
  'le_flash_tour',
  'address',
] as const;

export type TypeaheadType = (typeof TYPEAHEAD_TYPES)[number];

export type TypeaheadQueryParams = {
  type: Array<TypeaheadType>;
  brand: string;
  region: string;
  limit: number;
  vertical?: string;
  priority: 'current' | 'next';
};

export type TypeaheadPriority = TypeaheadQueryParams['priority'];

export type TypeaheadBrand = TypeaheadQueryParams['brand'];

export type ImagePutPayload =
  operations['/api/search/admin/v1/image/put']['requestBody']['content']['application/json'];

export type ImageResponse =
  operations['/api/search/admin/v1/image/{placeId}/get']['responses']['200']['content']['application/json'];

export type TrendingDestinationsResponse =
  operations['/api/search/place/v1/trending/get']['responses']['200']['content']['application/json']['result'][number];

export type PropertyManualLocationsListResponse =
  operations['/api/search/admin/v1/property-manual-locations/get']['responses']['200']['content']['application/json'];

export type PropertyManualLocationsDeleteResponse =
  operations['/api/search/admin/v1/property-manual-locations/{id}/delete']['responses']['200']['content']['application/json'];

export type PropertyManualLocationsPatchResponse =
  operations['/api/search/admin/v1/property-manual-locations/{id}/patch']['responses']['200']['content']['application/json'];

export type PropertyManualLocationsPutResponse =
  operations['/api/search/admin/v1/property-manual-locations/put']['responses']['200']['content']['application/json'];

export type PropertyManualLocation = PropertyManualLocationsListResponse['result'];

export type ExperienceManualLocationsListResponse =
  operations['/api/search/admin/v1/experience-manual-locations/get']['responses']['200']['content']['application/json'];

export type ExperienceManualLocationsPutResponse =
  operations['/api/search/admin/v1/experience-manual-locations/put']['responses']['200']['content']['application/json'];

export type ExperienceManualLocationsDeleteResponse =
  operations['/api/search/admin/v1/experience-manual-locations/delete']['responses']['200']['content']['application/json'];

export type ExperienceManualLocation = ExperienceManualLocationsListResponse['result'];

export type TrendingPlacePostResponse =
  operations['/api/search/admin/v1/trending-places/post']['responses']['200']['content']['application/json'];

export type TrendingPlaceGetResponse =
  operations['/api/search/admin/v1/trending-places/get']['responses']['200']['content']['application/json'];

export type UserRecentSearchesResponse =
  operations['/api/search/admin/v1/users/{userId}/recent-searches/get']['responses']['200']['content']['application/json'];

export type UserRecentSearch = UserRecentSearchesResponse['result'][number];

export type UserRecentOfferPageViewsResponse =
  operations['/api/search/admin/v1/users/{userId}/recent-offer-page-views/get']['responses']['200']['content']['application/json'];

export type UserRecentOfferPageView =
  operations['/api/search/admin/v1/users/{userId}/recent-offer-page-views/get']['responses']['200']['content']['application/json']['result'][number];

export const ANYWHERE_PLACE_ID = 'le_d41d8cd98f00b204e9800998ecf8427e';

export enum EvVariant {
  Default = 'default',
}

export interface PatchHotelScoreEntry {
  region: string;
  variant: string;
  overrideScore: number;
  overrideExpireDate: string;
}

export interface PutCruiseRecommendation {
  id: string;
  recommendation: number;
}

interface SearchLightOptions {
  region?: string;
  destinationId?: string;
  offerTypes?: Array<string>;
  brand?: string;
  noCache?: boolean;
  limit?: number;
  isLuxPlusMember?: boolean;
  signal?: AbortSignal;
}

interface EvOptions {
  vertical: string;
  offerId: string;
  region: string;
  variant: string;
}

export const FlashLpcOfferTypes = ['hotel', 'tactical_ao_hotel'];

export const AllHotelOfferTypes = [...FlashLpcOfferTypes, 'bundle_and_save', 'rental', 'bedbank_hotel'];

export default class SearchService {
  static typeaheadPath() {
    return `${window.configs.API_HOST}/api/search/typeahead`;
  }

  static placePath() {
    return `${window.configs.API_HOST}/api/search/admin/v1/place`;
  }

  static placeDetailsPath() {
    return `${window.configs.API_HOST}/api/search/place/v1/places`;
  }

  static typeaheadDeprioritisationPath() {
    return `${window.configs.API_HOST}/api/search/admin/v1/typeahead/deprioritisations`;
  }

  static lePropertyPath() {
    return `${window.configs.API_HOST}/api/search/admin/v1/le-property`;
  }

  static bedbankPropertyPath() {
    return `${window.configs.API_HOST}/api/search/admin/v1/bedbank-property`;
  }

  static hotelScorePath() {
    return `${window.configs.API_HOST}/api/search/admin/v1/hotel/scores`;
  }

  static nearbyHotelOffersPath() {
    return `${window.configs.API_HOST}/api/search/admin/v1/hotel-offers/nearby`;
  }

  static offerScorePath(vertical: string) {
    return `${window.configs.API_HOST}/api/search/admin/v1/${vertical}/scores`;
  }

  static destinationImage(placeId: string) {
    return `${window.configs.API_HOST}/api/search/admin/v1/image/${placeId}`;
  }

  static offerScoreSettingsPath(vertical: string) {
    return `${window.configs.API_HOST}/api/search/admin/v1/${vertical}/scoreSettings`;
  }

  static cruiseRecommendationPath() {
    return `${window.configs.API_HOST}/api/search/admin/v1/cruise/recommendation`;
  }

  static listOrderPath(listName: string) {
    return `${window.configs.API_HOST}/api/search/admin/v1/list-order/${listName}`;
  }

  static unifiedOfferListPath() {
    return `${window.configs.API_HOST}/api/search/unified/v1/list`;
  }

  static typeaheadTemplatePath() {
    return `${window.configs.API_HOST}/api/search/admin/v1/typeahead/template`;
  }

  static placeAncestorsPath(placeId: string) {
    return `${window.configs.API_HOST}/api/search/place/v1/places/${placeId}/ancestors`;
  }

  static getPlaceAncestors(placeId: string): Promise<PlaceAncestorsResponse> {
    return request(`${this.placeAncestorsPath(placeId)}`, {
      method: 'GET',
    });
  }

  static typeahead(query: string, queryParams: TypeaheadQueryParams): Promise<TypeaheadResponse> {
    return request(`${this.typeaheadPath()}/${query}?${qs.stringify(queryParams)}`, {
      method: 'GET',
    });
  }

  static getPlace(placeId: string): Promise<GetPlaceResponse> {
    return request(`${this.placePath()}/${placeId}`, {
      method: 'GET',
    });
  }

  static getPlaceDetails(placeId: string): Promise<GetPlaceDetailsResponse> {
    return request(`${this.placeDetailsPath()}/${placeId}`, {
      method: 'GET',
    });
  }

  static async getPlacesByIds(placeIds: Array<string>, options?: { signal?: AbortSignal }): Promise<GetPlacesResponse> {
    const uniqueIds = Array.from(new Set(placeIds.filter(isLePlaceId)));
    if (uniqueIds.length === 0) {
      return { result: [] } as GetPlacesResponse;
    }

    const responses = await Promise.all(
      chunk(uniqueIds, PLACE_LOOKUP_CHUNK_SIZE).map(
        (chunkIds) =>
          request(
            `${this.placeDetailsPath()}?${qs.stringify({
              ids: chunkIds.join(','),
              include: 'geom',
            })}`,
            {
              method: 'GET',
              signal: options?.signal,
            },
          ) as Promise<GetPlacesResponse>,
      ),
    );

    return {
      ...responses[0],
      result: responses.flatMap((response) => response.result ?? []),
    };
  }

  static getTypeaheadDeprioritisation(placeId: string) {
    return request(`${this.typeaheadDeprioritisationPath()}/${placeId}`, {
      method: 'GET',
    });
  }

  static getTypeaheadDeprioritisations(): Promise<GetTypeaheadDeprioritisationsResponse> {
    return request(this.typeaheadDeprioritisationPath(), {
      method: 'GET',
    });
  }

  static putTypeaheadDeprioritisation(placeId: string) {
    return request(`${this.typeaheadDeprioritisationPath()}/${placeId}`, {
      method: 'PUT',
    });
  }

  static deleteTypeaheadDeprioritisation(placeId: string) {
    return request(`${this.typeaheadDeprioritisationPath()}/${placeId}`, {
      method: 'DELETE',
    });
  }

  static getLeProperty(placeId: string, options?: { signal?: AbortSignal }) {
    return request(`${this.lePropertyPath()}/${placeId}`, {
      method: 'GET',
      signal: options?.signal,
    });
  }

  static getAllTypeaheadTemplates() {
    return request(this.typeaheadTemplatePath(), {
      method: 'GET',
    });
  }

  static getTypeaheadTemplate(templateId: string): Promise<App.TypeaheadSearchTemplate> {
    return request(`${this.typeaheadTemplatePath()}/${templateId}`, {
      method: 'GET',
    }).then((response) => response.result);
  }

  static putTypeaheadTemplate(template: App.TypeaheadSearchTemplate) {
    return request(this.typeaheadTemplatePath(), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(template),
    });
  }

  static hideTypeaheadTemplate(templateId: string) {
    return request(`${this.typeaheadTemplatePath()}/${templateId}/set-hidden`, {
      method: 'PATCH',
    });
  }

  static async getSearchLightOffers({
    region = 'AU',
    brand = 'luxuryescapes',
    offerTypes = [],
    destinationId = ANYWHERE_PLACE_ID,
    limit = 100,
    noCache,
    isLuxPlusMember,
    signal,
  }: SearchLightOptions): Promise<HotelSearchList> {
    const url = new URL('/api/search/v1/light', window.configs.API_HOST);
    url.searchParams.set('placeIds', destinationId);
    url.searchParams.set('region', region);
    url.searchParams.set('brand', brand);
    url.searchParams.set('limit', limit.toString());

    if (isLuxPlusMember) {
      url.searchParams.set('isLuxPlusMember', 'true');
    }

    if (noCache) {
      url.searchParams.set('noCache', 'true');
    }

    if (offerTypes.length > 0) {
      url.searchParams.set('offerType', offerTypes.join(','));
    }

    const response = await request(url.toString(), {
      method: 'GET',
      signal,
    });
    return response.result;
  }

  static getHotelGlobalRanks = memoize(
    async ({ region, brand, isLuxPlusMember }: { region: string; brand: string; isLuxPlusMember: boolean }) => {
      const anywhereList = await this.getSearchLightOffers({
        region,
        brand,
        limit: 1000,
        noCache: true,
        isLuxPlusMember,
      });

      const offerPositions: GlobalRanks = {};
      anywhereList.forEach((offer, index) => {
        offerPositions[offer.id] = index + 1;
      });

      return offerPositions;
    },
    ({ region, brand, isLuxPlusMember }) => `${region}-${brand}-${isLuxPlusMember}`,
  );

  static getAllUltraLuxOffers(
    region = 'AU',
    destinationId = ANYWHERE_PLACE_ID,
  ): Promise<{ results: Array<Exclude<OrderOffer, { vertical: 'ev' }>> }> {
    const url = new URL('/api/search/unified/v1/list', window.configs.API_HOST);
    url.searchParams.set('offerTypes', 'hotel,tactical_ao_hotel');
    url.searchParams.set('placeIds', destinationId);
    url.searchParams.set('region', region);
    url.searchParams.set('brand', 'luxuryescapes');
    url.searchParams.set('holidayTypesScoped', 'Ultra%20Lux');

    return request(url.toString(), {
      method: 'GET',
    });
  }

  static async getAllTourOffers(
    region: string,
    destinationId: string,
    sorting: string,
  ): Promise<{
    result: TourSearchList;
  }> {
    return sorting === 'default'
      ? this.getAllTourOffersFromSearchList(destinationId, region)
      : this.getAllTourOffersFromUnifiedEndpoint(destinationId, region, sorting);
  }

  private static async getAllTourOffersFromSearchList(destinationId: string, region: string) {
    const url = new URL('/api/search/tour/v1/list', window.configs.API_HOST);

    url.searchParams.set('offerType', 'tour_v2');
    url.searchParams.set('placeIds[0]', destinationId);
    url.searchParams.set('region', region);
    url.searchParams.set('brand', 'luxuryescapes');
    url.searchParams.set('sortBy', 'vertical');
    url.searchParams.set('isFacet', 'true');

    const response = await request(url.toString(), {
      method: 'GET',
    });
    return {
      result: response.result.map((tourOptionId) => tourOptionId.slice(0, 41)),
    };
  }

  private static async getAllTourOffersFromUnifiedEndpoint(destinationId: string, region: string, sorting: string) {
    const tourOfferTypes = ['tour_v2'];

    const options: UnifiedListProps = {
      brand: 'luxuryescapes',
      region,
      placeIds: destinationId,
      offerTypes: tourOfferTypes.join(','),
      sortBy: sorting,
      limit: 2000,
    };

    return this.getUnifiedList(options).then((response) => ({
      result: response.results.map((tour) => tour.bk),
    }));
  }

  static async getAllCruiseOffers(
    region: string,
    destinationId: string,
    sorting: string,
  ): Promise<{
    result: Array<OrderOffer>;
  }> {
    return sorting === 'default'
      ? this.getAllCruiseOffersFromSearchList(destinationId, region)
      : this.getAllCruiseOffersFromUnifiedEndpoint(destinationId, region, sorting);
  }

  private static async getAllCruiseOffersFromSearchList(
    destinationId: string,
    region: string,
  ): Promise<{
    result: Array<OrderOffer>;
  }> {
    const url = new URL('/api/search/cruise/v1/list', window.configs.API_HOST);

    const response = await request(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        arrivalPlaceId: destinationId,
        region,
        brand: 'luxuryescapes',
      }),
    });
    return {
      result: response.result.map((offerId) => ({
        vertical: 'cruise',
        offerType: 'cruise',
        bk: offerId,
      })),
    };
  }

  private static async getAllCruiseOffersFromUnifiedEndpoint(destinationId: string, region: string, sorting: string) {
    const options: UnifiedListProps = {
      brand: 'luxuryescapes',
      region,
      placeIds: destinationId,
      offerTypes: 'cruise,tour,hotel',
      holidayTypesScoped: 'Cruises',
      sortBy: sorting === 'default' ? 'vertical-cruise' : sorting,
      limit: 2000,
    };

    return this.getUnifiedList(options).then((response) => ({
      result: response.results,
    }));
  }

  static async getAllExperienceOffers(
    brand: string,
    region: string,
    destinationId: string,
    sorting: string,
  ): Promise<{
    result: Array<OrderOffer>;
  }> {
    return sorting === 'default'
      ? this.getAllExperienceOffersFromSearchList(destinationId, brand, region)
      : this.getAllExperienceOffersFromUnifiedEndpoint(destinationId, brand, region, sorting);
  }

  private static async getAllExperienceOffersFromSearchList(
    destinationId: string,
    brand: string,
    region: string,
  ): Promise<{
    result: Array<OrderOffer>;
  }> {
    const url = new URL('/api/search/experience/v1/list', window.configs.API_HOST);

    url.searchParams.set('placeId', destinationId);
    url.searchParams.set('region', region);
    url.searchParams.set('brand', brand);
    url.searchParams.set('sortBy', 'type');

    const response = await request(url.toString(), {
      method: 'GET',
    });
    return {
      result: response.result.map((offerId) => ({
        vertical: 'experience',
        offerType: 'flash',
        bk: offerId,
      })),
    };
  }

  private static async getAllExperienceOffersFromUnifiedEndpoint(
    destinationId: string,
    brand: string,
    region: string,
    sorting: string,
  ): Promise<{
    result: Array<OrderOffer>;
  }> {
    const experienceOfferTypes: Array<UnifiedSearchOfferType> = ['flash', 'always_on', 'channel_manager'];

    const options: UnifiedListProps = {
      brand,
      region,
      placeIds: destinationId,
      offerTypes: experienceOfferTypes.join(','),
      sortBy: sorting,
      limit: 2000,
    };

    return this.getUnifiedList(options).then((response) => ({
      result: response.results,
    }));
  }

  static async getAllUnifiedOffers(
    region: string,
    destinationId: string,
    sorting: string,
  ): Promise<{
    results: UnifiedSearchList;
  }> {
    const hotelOfferTypes: Array<UnifiedSearchOfferType> = ['hotel', 'tactical_ao_hotel', 'bedbank_hotel', 'rental'];
    const tourOfferTypes: Array<UnifiedSearchOfferType> = ['tour', 'tour_v2'];
    const cruiseOfferTypes: Array<UnifiedSearchOfferType> = ['cruise'];
    const experienceOfferTypes: Array<UnifiedSearchOfferType> = ['flash', 'always_on', 'channel_manager'];

    const offerTypes: Array<UnifiedSearchOfferType> = [
      ...hotelOfferTypes,
      ...tourOfferTypes,
      ...cruiseOfferTypes,
      ...experienceOfferTypes,
    ];

    const sorts = {
      default: 'home',
      ev: 'ev',
    };

    const options: UnifiedListProps = {
      brand: 'luxuryescapes',
      region,
      placeIds: destinationId,
      offerTypes: offerTypes.join(','),
      limit: 2000,
      sortBy: sorts[sorting],
    };

    return this.getUnifiedList(options);
  }

  static getBedbankProperty(placeId: string, options?: { signal?: AbortSignal }) {
    return request(`${this.bedbankPropertyPath()}/${placeId}`, {
      method: 'GET',
      signal: options?.signal,
    });
  }

  static getNearbyHotelOffers(
    params: NearbyHotelOffersParams,
    options?: { signal?: AbortSignal },
  ): Promise<NearbyHotelOffersResponse> {
    const url = `${this.nearbyHotelOffersPath()}?${qs.stringify(params)}`;

    return request(url, {
      method: 'GET',
      signal: options?.signal,
    });
  }

  static getHotelScore(offerId: string): Promise<HotelScoreResponse> {
    return request(`${this.hotelScorePath()}/${offerId}`, {
      method: 'GET',
    });
  }

  static refreshHotelScore(offerId: string) {
    return request(`${this.hotelScorePath()}/${offerId}/refresh`, {
      method: 'POST',
    });
  }

  static getHotelScoreForVariant(
    offerId: string,
    region: string,
    variant: string,
  ): Promise<HotelScoreForVariantResponse> {
    return request(`${this.hotelScorePath()}/${offerId}/${region}/${variant}`, {
      method: 'GET',
    });
  }

  static patchOfferScore(vertical: string, offerId: string, payload: Array<PatchHotelScoreEntry>) {
    return request(`${this.offerScorePath(vertical)}/${offerId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  }

  static getOfferScoreForVariant({ vertical, offerId, region, variant }: EvOptions) {
    return request(`${this.offerScorePath(vertical)}/${offerId}/${region}/${variant}`, {
      method: 'GET',
    });
  }

  static getOfferScoreSettings(vertical: string, region: string) {
    return request(`${this.offerScoreSettingsPath(vertical)}?region=${region}`, {
      method: 'GET',
    });
  }

  static putDestinationImage(payload: ImagePutPayload) {
    return request(`${this.destinationImage('')}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  }

  static getDestinationImage(placeId: string): Promise<ImageResponse> {
    return request(`${this.destinationImage(placeId)}`, {
      method: 'GET',
    });
  }

  static putCruiseRecommendation(payload: Array<PutCruiseRecommendation>) {
    return request(this.cruiseRecommendationPath(), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  }

  static getCruisesRecommendation() {
    return request(this.cruiseRecommendationPath(), {
      method: 'GET',
    });
  }

  static getListOrder(
    listName: string,
    brand: string,
    region: string,
  ): Promise<{ result: { offers: Array<OrderOffer>; revision?: string } }> {
    return request(`${this.listOrderPath(listName)}?brand=${brand}&region=${region}`, {
      method: 'GET',
    });
  }

  static putListOrder(listName: string, offers: Array<OrderOffer>, brand: string, region: string, revision?: string) {
    return request(this.listOrderPath(listName), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        brand,
        region,
        offers,
        ...(revision && { revision }),
      }),
    });
  }

  static getUnifiedList(params: UnifiedListProps): Promise<UnifiedSearchListResponse> {
    const queryString = qs.stringify(params);

    return request(`${this.unifiedOfferListPath()}?${queryString}`, {
      method: 'GET',
    });
  }

  static getTrendingDestinations(region: string, brand = 'luxuryescapes', limit?: number, withImage = false) {
    return request(
      `${window.configs.API_HOST}/api/search/place/v1/trending?region=${region}&brand=${brand}&includeImage=${withImage}` +
        (limit ? `&limit=${limit}` : ''),
      {
        method: 'GET',
      },
    );
  }

  static getOfferAssociationList(): Promise<PropertyManualLocationsListResponse> {
    return request(`${window.configs.API_HOST}/api/search/admin/v1/property-manual-locations`, {
      method: 'GET',
    });
  }

  static getTypeahead(query: string, queryParams): Promise<TypeaheadSearchResponse> {
    return request(`${window.configs.API_HOST}/api/search/typeahead/${query}?${qs.stringify(queryParams)}`, {
      method: 'GET',
    });
  }

  static putOfferAssociationList(
    propertyId,
    placeId,
    lastModifiedBy,
    notes,
  ): Promise<PropertyManualLocationsPutResponse> {
    return request(`${window.configs.API_HOST}/api/search/admin/v1/property-manual-locations`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        propertyId,
        placeId,
        lastModifiedBy,
        notes,
      }),
    });
  }

  static deleteOfferAssociationList(id: string): Promise<PropertyManualLocationsDeleteResponse> {
    return request(`${window.configs.API_HOST}/api/search/admin/v1/property-manual-locations/${id}`, {
      method: 'DELETE',
    });
  }

  static patchOfferAssociationList(
    id,
    propertyId,
    placeId,
    lastModifiedBy,
    notes,
  ): Promise<PropertyManualLocationsPatchResponse> {
    return request(`${window.configs.API_HOST}/api/search/admin/v1/property-manual-locations/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        propertyId,
        placeId,
        lastModifiedBy,
        notes,
      }),
    });
  }

  static getTrendingPlace(brand: string, region: string, signal?: AbortSignal): Promise<TrendingPlaceGetResponse> {
    return request(`${window.configs.API_HOST}/api/search/admin/v1/trending-places?region=${region}&brand=${brand}`, {
      method: 'GET',
      signal,
    });
  }

  static upsertTrendingPlace(
    placeId: string,
    brand: string,
    region: string,
    imageId?: string,
    manualPosition?: number | null,
    enforcedPosition?: boolean,
  ): Promise<TrendingPlacePostResponse> {
    return request(`${window.configs.API_HOST}/api/search/admin/v1/trending-places`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        placeId,
        manualPosition,
        brand,
        region,
        imageId,
        enforcedPosition,
      }),
    });
  }

  static updatePlaceImage(
    placeId: string,
    brand: string,
    region: string,
    imageId: string,
  ): Promise<TrendingPlaceGetResponse> {
    return request(`${window.configs.API_HOST}/api/search/admin/v1/trending-places/${placeId}/image`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        brand,
        region,
        imageId,
      }),
    });
  }

  static getUserRecentSearches(userId: string, brand: string): Promise<UserRecentSearchesResponse> {
    return request(`${window.configs.API_HOST}/api/search/admin/v1/users/${userId}/recent-searches?brand=${brand}`, {
      method: 'GET',
    });
  }

  static getUserRecentOfferPageViews(userId: string, brand: string): Promise<UserRecentOfferPageViewsResponse> {
    return request(
      `${window.configs.API_HOST}/api/search/admin/v1/users/${userId}/recent-offer-page-views?brand=${brand}`,
      {
        method: 'GET',
      },
    );
  }

  static restoreSearch(restoreType: RestoreType): Promise<void> {
    const queryParams = {
      restoreType: [restoreType],
    };

    return request(`${window.configs.API_HOST}/api/search/admin/v1/restore?${qs.stringify(queryParams)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
  }

  static getExperienceManualLocations(): Promise<ExperienceManualLocationsListResponse> {
    return request(`${window.configs.API_HOST}/api/search/admin/v1/experience-manual-locations`, {
      method: 'GET',
    });
  }

  static putExperienceManualLocation(
    experienceOfferId: string,
    placeId: string,
    addedBy: string,
    comments?: string,
  ): Promise<ExperienceManualLocationsPutResponse> {
    return request(`${window.configs.API_HOST}/api/search/admin/v1/experience-manual-locations`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        experienceOfferId,
        placeId,
        addedBy,
        comments,
      }),
    });
  }

  static deleteExperienceManualLocation(
    experienceOfferId: string,
    placeId: string,
  ): Promise<ExperienceManualLocationsDeleteResponse> {
    return request(`${window.configs.API_HOST}/api/search/admin/v1/experience-manual-locations`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        experienceOfferId,
        placeId,
      }),
    });
  }

  static async getLuxPlusOffersBySavings(
    params: LuxPlusOffersBySavingsParams,
    options?: { signal?: AbortSignal },
  ): Promise<LuxPlusOffersBySavingsResponse> {
    const {
      brand = 'luxuryescapes',
      region = 'AU',
      priceMin,
      priceMax,
      savingMin,
      savingMax,
      limit = 1000,
      hotelTypes = LUX_PLUS_OFFERS_BY_SAVINGS_HOTEL_TYPES,
      reviewStatus = 'all',
    } = params;

    const offers = await this.getSearchLightOffers({
      region,
      brand,
      offerTypes: hotelTypes.length > 0 ? hotelTypes : LUX_PLUS_OFFERS_BY_SAVINGS_HOTEL_TYPES,
      limit,
      noCache: true,
      isLuxPlusMember: true,
      signal: options?.signal,
    });
    const currencyCode = getCurrencyCodeByRegionCode(region) ?? 'AUD';
    const rows = offers
      .map((offer): LuxPlusOfferBySavings | undefined => {
        if (offer.vertical !== 'hotel' || offer.status !== 'AVAILABLE' || offer.available === false) {
          return undefined;
        }

        const lightOffer: SearchLightOffer = offer;
        const nonMemberPrice = lightOffer.lowestPrice;
        const luxPlusPrice = lightOffer.lowestMemberPrice;

        if (!nonMemberPrice || !luxPlusPrice) {
          return undefined;
        }

        const saving = nonMemberPrice - luxPlusPrice;
        const reviewSavingThreshold = getLuxPlusReviewSavingThreshold(nonMemberPrice);
        const flaggedForReview = reviewSavingThreshold !== undefined && saving <= reviewSavingThreshold;

        if (saving <= 0) {
          return undefined;
        }

        if (priceMin !== undefined && nonMemberPrice < priceMin) {
          return undefined;
        }

        if (priceMax !== undefined && nonMemberPrice > priceMax) {
          return undefined;
        }

        if (savingMin !== undefined && saving < savingMin) {
          return undefined;
        }

        if (savingMax !== undefined && saving > savingMax) {
          return undefined;
        }

        if (reviewStatus === 'flagged' && !flaggedForReview) {
          return undefined;
        }

        if (reviewStatus === 'not_flagged' && flaggedForReview) {
          return undefined;
        }

        return {
          offerId: lightOffer.id,
          offerName: lightOffer.name ?? lightOffer.title ?? lightOffer.id,
          hotelTypeRaw: lightOffer.type,
          hotelType: LUX_PLUS_OFFERS_BY_SAVINGS_HOTEL_TYPE_LABELS[lightOffer.type],
          nonMemberPrice,
          luxPlusPrice,
          saving,
          flaggedForReview,
          reviewSavingThreshold,
          currencyCode,
          duration: lightOffer.duration,
          packageId: lightOffer.lowestPricePackageId,
          roomRateId: lightOffer.lowestPriceRoomRateId,
        };
      })
      .filter((offer): offer is LuxPlusOfferBySavings => offer !== undefined);

    const namesById = await getLuxPlusOfferNamesById({ rows, region, brand, signal: options?.signal });
    const enrichedRows = rows.map((row) => ({
      ...row,
      offerName: namesById.get(row.offerId) ?? row.offerName,
    }));

    return {
      status: 200,
      result: {
        offers: enrichedRows,
        total: enrichedRows.length,
      },
    };
  }

  static previewGeometryMerge(
    targetPlaceId: string,
    sourcePlaceId: string,
  ): Promise<{
    result: {
      target: { placeId: string; name: string; type: string; geojson: unknown; areaKm2: number };
      source: { placeId: string; name: string; type: string; geojson: unknown; areaKm2: number };
      merged: { geojson: unknown; areaKm2: number };
    };
  }> {
    return request(`${window.configs.API_HOST}/api/search/admin/v1/place/${targetPlaceId}/merge/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sourcePlaceId }),
    });
  }

  static applyGeometryMerge(targetPlaceId: string, sourcePlaceId: string): Promise<{ result: { geometryId: string } }> {
    return request(`${window.configs.API_HOST}/api/search/admin/v1/place/${targetPlaceId}/merge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sourcePlaceId }),
    });
  }
}

export interface UnifiedListProps {
  brand: string;
  region: string;
  placeIds: string;
  offerTypes: string;
  limit: number;
  holidayTypesScoped?: string;
  sortBy?: string;
  luxPlusOffersOnly?: boolean;
  campaigns?: string;
  holidayTypes?: string;
  cache?: boolean;
}

export type UnifiedSearchListResponse = {
  total: number;
  count: number;
  results: Array<Exclude<OrderOffer, { vertical: 'ev' }>>;
  filters?: {
    campaigns?: Record<string, { count: number }>;
    holidayTypes?: Record<string, { count: number }>;
  };
};

export interface TypeaheadSearchResponse {
  result: Array<TypeaheadSearchResult>;
}

export interface TypeaheadSearchResult {
  format: {
    mainText: string;
    secondaryText?: string;
    imageId?: string;
  };
  destinationType?: string;
  geoType?: string;
  id: string;
  name: string;
  searchType: string;
  offerType?: string;
  offerDiscount?: number;
  offerId?: string;
  hasTactical?: boolean;
  bedbankStatus?: string;
  bedbankHasPromotions?: boolean;
  fk_location?: string;
  country_code?: string | null;
}

interface SearchTypeaheadParams {
  types: Array<string>;
  brand: string;
  region: string;
  limit: number;
  priority: string;
}

export async function searchTypeahead(query: string, params: SearchTypeaheadParams) {
  const queryParams = {
    ...params,
    type: params.types,
  };
  delete queryParams.types;

  const queryString = qs.stringify(queryParams);
  const response = await fetch(`${window.configs.API_HOST}/api/search/typeahead/${query}?${queryString}`);

  if (!response.ok) {
    throw new Error('Failed to search locations');
  }

  return response.json();
}
